"use client";

import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import api, { userAPI } from "../../services/api.js";

const SearchModal = ({ isOpen, onClose, userType = "PET_GUARDIAN" }) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const [recentSearches, setRecentSearches] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchError, setSearchError] = useState(null);

  // Color schemes based on user type
  const getColorScheme = (userType) => {
    const type = userType?.toUpperCase();
    switch (type) {
      case "PET_GUARDIAN":
        return { primary: "#8548A8", hover: "#7A4397" };
      case "VETERINARIAN":
      case "VET":
        return { primary: "#00D4B3", hover: "#00C4A7" };
      case "SERVICE_PROVIDER":
      case "BUSINESS":
        return { primary: "#D65745", hover: "#C7493A" };
      default:
        return { primary: "#8548A8", hover: "#7A4397" };
    }
  };

  const colors = getColorScheme(userType);

  // Load recent searches from localStorage on component mount
  useEffect(() => {
    const loadRecentSearches = () => {
      try {
        const savedSearches = localStorage.getItem("sulhafa_recent_searches");
        if (savedSearches) {
          const parsedSearches = JSON.parse(savedSearches);
          setRecentSearches(
            Array.isArray(parsedSearches) ? parsedSearches : []
          );
        }
      } catch (error) {
        console.error("Error loading recent searches:", error);
        setRecentSearches([]);
      }
    };

    loadRecentSearches();
  }, []);

  // Save searches to localStorage
  const saveSearchesToStorage = (searches) => {
    try {
      localStorage.setItem("sulhafa_recent_searches", JSON.stringify(searches));
    } catch (error) {
      console.error("Error saving recent searches:", error);
    }
  };

  // Client-side filtering function for search results
  const filterSearchResults = (results, query) => {
    if (!query || !query.trim()) {
      return results;
    }
    const searchLower = query.trim().toLowerCase();
    return results.filter((user) => {
      const firstName = (user.first_name || "").toLowerCase();
      const lastName = (user.last_name || "").toLowerCase();
      const username = (user.username || "").toLowerCase();

      // Check if search matches anywhere in first name, last name, or username
      return (
        firstName.includes(searchLower) ||
        lastName.includes(searchLower) ||
        username.includes(searchLower)
      );
    });
  };

  const handleSearch = async (query) => {
    if (query.trim()) {
      setIsSearching(true);
      setSearchError(null);

      // Add to recent searches if not already present
      const trimmedQuery = query.trim();
      if (!recentSearches.includes(trimmedQuery)) {
        const updatedSearches = [trimmedQuery, ...recentSearches.slice(0, 4)];
        setRecentSearches(updatedSearches);
        saveSearchesToStorage(updatedSearches);
      }

      try {
        const response = await userAPI.searchUsers({
          query: trimmedQuery
        });

        if (response.data.status === "success") {
          // Filter out ADMIN and CLINIC_MODERATOR users from search results
          // CLINIC_MODERATOR users should only appear in clinic team management
          const filteredResults = (response.data.data || []).filter(
            user =>
              user.user_type !== 'ADMIN' &&
              user.user_type !== 'admin' &&
              user.user_type !== 'CLINIC_MODERATOR' &&
              user.user_type !== 'clinic_moderator'
          );
          setSearchResults(filteredResults);
        } else {
          setSearchError("Search failed. Please try again.");
          setSearchResults([]);
        }
      } catch (error) {
        console.error("Search error:", error);
        setSearchError("Search failed. Please try again.");
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }
  };

  const handleRecentSearchClick = (search) => {
    setSearchQuery(search);
    handleSearch(search);
  };

  const removeRecentSearch = (searchToRemove) => {
    const updatedSearches = recentSearches.filter(
      (search) => search !== searchToRemove
    );
    setRecentSearches(updatedSearches);
    saveSearchesToStorage(updatedSearches);
  };

  const clearAllRecentSearches = () => {
    setRecentSearches([]);
    saveSearchesToStorage([]);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch(searchQuery);
    }
    if (e.key === "Escape") {
      onClose();
    }
  };

  // Close modal when clicking outside
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Clear search results when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery("");
      setSearchResults([]);
      setSearchError(null);
    }
  }, [isOpen]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      onClick={handleBackdropClick}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        zIndex: 9999,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        paddingTop: "80px",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: "700px",
          height: "calc(100vh - 80px)",
          background: "#FEFEFB",
          borderRadius: "12px 12px 0 0",
          fontFamily: '"Red Hat Display", sans-serif',
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          margin: "0 20px",
        }}
      >
        {/* Search Header */}
        <div
          style={{
            padding: "24px",
            background: "#FFFFFF",
            borderBottom: "1px solid #E5E7EB",
            display: "flex",
            alignItems: "center",
            gap: "16px",
          }}
        >
          {/* Back Button */}
          <button
            onClick={onClose}
            style={{
              width: "40px",
              height: "40px",
              borderRadius: "50%",
              border: "none",
              background: "#F3F4F6",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "background 0.2s ease",
            }}
            onMouseEnter={(e) => (e.target.style.background = "#E5E7EB")}
            onMouseLeave={(e) => (e.target.style.background = "#F3F4F6")}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="#374151">
              <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
            </svg>
          </button>

          {/* Search Input */}
          <div
            style={{
              flex: 1,
              position: "relative",
            }}
          >
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={t("navbar.SearchUser")}
              style={{
                width: "100%",
                padding: "12px 20px 12px 48px",
                border: "2px solid #E5E7EB",
                borderRadius: "24px",
                fontSize: "16px",
                fontFamily: '"Red Hat Display", sans-serif',
                outline: "none",
                background: "#F9FAFB",
                transition: "all 0.3s ease",
              }}
              onFocus={(e) => {
                e.target.style.borderColor = colors.primary;
                e.target.style.background = "#FFFFFF";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = "#E5E7EB";
                e.target.style.background = "#F9FAFB";
              }}
              autoFocus
            />

            {/* Search Icon */}
            <div
              style={{
                position: "absolute",
                left: "16px",
                top: "50%",
                transform: "translateY(-50%)",
                pointerEvents: "none",
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="#9CA3AF">
                <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
              </svg>
            </div>
          </div>
        </div>

        {/* Search Content */}
        <div
          style={{
            flex: 1,
            padding: "24px",
            overflow: "auto",
          }}
        >
          {/* Search Results */}
          {searchQuery && !isSearching && searchResults.length > 0 && (
            <div>
              <h3
                style={{
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontWeight: 600,
                  fontSize: "18px",
                  lineHeight: "27px",
                  color: "#303E4E",
                  margin: "0 0 20px 0",
                }}
              >
                Search Results ({filterSearchResults(searchResults, searchQuery).length})
              </h3>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                }}
              >
                {filterSearchResults(searchResults, searchQuery).map((user) => (
                  <div
                    key={user.user_id || user.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "16px",
                      padding: "16px",
                      borderRadius: "12px",
                      background: "#FFFFFF",
                      border: "1px solid #E5E7EB",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.borderColor = colors.primary;
                      e.target.style.boxShadow =
                        "0 4px 12px rgba(0, 0, 0, 0.1)";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.borderColor = "#E5E7EB";
                      e.target.style.boxShadow = "none";
                    }}
                    onClick={() => {
                      // Handle user click - navigate to profile or open chat
                    }}
                  >
                    {/* User Avatar */}
                    <div
                      style={{
                        width: "48px",
                        height: "48px",
                        borderRadius: "50%",
                        overflow: "hidden",
                        background: "#F3F4F6",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {user.profile_image_url ? (
                        <img
                          src={user.profile_image_url}
                          alt={`${user.first_name} ${user.last_name}`}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            border: `2px solid ${getColorScheme(user.user_type).primary}`,
                            borderRadius: "50%"
                          }}
                          onError={(e) => {
                            e.target.style.display = "none";
                            e.target.nextSibling.style.display = "flex";
                          }}
                        />
                      ) : null}
                      <div
                        style={{
                          width: "100%",
                          height: "100%",
                          display: user.profile_image_url ? "none" : "flex",
                           alignItems: "center",
                           justifyContent: "center",
                           background: getColorScheme(user.user_type).primary,
                           border: `2px solid ${getColorScheme(user.user_type).primary}`,
                           borderRadius: "50%",
                           color: "#FFFFFF",
                           fontSize: "18px",
                           fontWeight: 600,
                         }}
                      >
                        {user.first_name?.charAt(0)?.toUpperCase() || "U"}
                      </div>
                    </div>

                    {/* User Info */}
                    <div
                      style={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        gap: "4px",
                      }}
                    >
                      <div
                        style={{
                          fontFamily: '"Red Hat Display", sans-serif',
                          fontWeight: 600,
                          fontSize: "16px",
                          lineHeight: "24px",
                          color: "#303E4E",
                        }}
                      >
                        {user.full_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username || "User"}
                      </div>

                      {/* <div
                        style={{
                          fontFamily: '"Red Hat Display", sans-serif',
                          fontWeight: 400,
                          fontSize: "14px",
                          lineHeight: "20px",
                          color: "#6B7280",
                        }}
                      >
                        {user.username ? `@${user.username}` : (user.user_type || 'User')}
                      </div> */}

                      {user.city_data?.name && (
                        <div
                          style={{
                            fontFamily: '"Red Hat Display", sans-serif',
                            fontWeight: 400,
                            fontSize: "12px",
                            lineHeight: "16px",
                            color: "#9CA3AF",
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 24 24"
                            fill="#9CA3AF"
                          >
                            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
                          </svg>
                          {user.city_data.name}
                          {user.country_data?.name &&
                            `, ${user.country_data.name}`}
                        </div>
                      )}
                    </div>

                    {/* Action Button */}
                    <button
                      style={{
                        padding: "8px 16px",
                        borderRadius: "20px",
                        border: "none",
                        background: colors.primary,
                        color: "#FFFFFF",
                        fontSize: "14px",
                        fontWeight: 500,
                        cursor: "pointer",
                        transition: "background 0.2s ease",
                      }}
                      onMouseEnter={(e) =>
                        (e.target.style.background = colors.hover)
                      }
                      onMouseLeave={(e) =>
                        (e.target.style.background = colors.primary)
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        window.location.href = `/profile/${user.user_id || user.id}`;
                      }}
                    >
                      View Profile
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* No Results */}
          {searchQuery &&
            !isSearching &&
            searchResults.length === 0 &&
            !searchError && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "60px 20px",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    width: "80px",
                    height: "80px",
                    background: "#F3F4F6",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: "24px",
                  }}
                >
                  <svg
                    width="40"
                    height="40"
                    viewBox="0 0 24 24"
                    fill="#9CA3AF"
                  >
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                  </svg>
                </div>

                <h3
                  style={{
                    fontFamily: '"Red Hat Display", sans-serif',
                    fontWeight: 600,
                    fontSize: "20px",
                    lineHeight: "30px",
                    color: "#303E4E",
                    margin: "0 0 8px 0",
                  }}
                >
                  {t("navbar.No_Users_Found")}
                </h3>

                <p
                  style={{
                    fontFamily: '"Red Hat Display", sans-serif',
                    fontWeight: 400,
                    fontSize: "16px",
                    lineHeight: "24px",
                    color: "#6B7280",
                    margin: 0,
                    maxWidth: "300px",
                  }}
                >
                  {t("navbar.Different_Keywords")}
                </p>
              </div>
            )}

          {/* Search Error */}
          {searchError && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: "60px 20px",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  width: "80px",
                  height: "80px",
                  background: "#FEF2F2",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: "24px",
                }}
              >
                <svg width="40" height="40" viewBox="0 0 24 24" fill="#EF4444">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                </svg>
              </div>

              <h3
                style={{
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontWeight: 600,
                  fontSize: "20px",
                  lineHeight: "30px",
                  color: "#303E4E",
                  margin: "0 0 8px 0",
                }}
              >
                Search failed
              </h3>

              <p
                style={{
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontWeight: 400,
                  fontSize: "16px",
                  lineHeight: "24px",
                  color: "#6B7280",
                  margin: 0,
                  maxWidth: "300px",
                }}
              >
                {searchError}
              </p>
            </div>
          )}

          {/* Recent Searches */}
          {recentSearches.length > 0 && !searchQuery && !isSearching && (
            <div>
              {/* Header */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "20px",
                }}
              >
                <h3
                  style={{
                    fontFamily: '"Red Hat Display", sans-serif',
                    fontWeight: 600,
                    fontSize: "18px",
                    lineHeight: "27px",
                    color: "#303E4E",
                    margin: 0,
                  }}
                >
                  Recent searches
                </h3>

                <button
                  onClick={clearAllRecentSearches}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#9CA3AF",
                    fontSize: "20px",
                    fontWeight: 500,
                    cursor: "pointer",
                    fontFamily: '"Red Hat Display", sans-serif',
                    padding: "4px",
                    borderRadius: "4px",
                    transition: "color 0.2s ease",
                  }}
                  onMouseEnter={(e) => (e.target.style.color = "#6B7280")}
                  onMouseLeave={(e) => (e.target.style.color = "#9CA3AF")}
                >
                  ×
                </button>
              </div>

              {/* Recent Search Items */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "4px",
                }}
              >
                {recentSearches.map((search, index) => (
                  <div
                    key={index}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "12px 16px",
                      borderRadius: "8px",
                      transition: "background 0.2s ease",
                      cursor: "pointer",
                    }}
                    onMouseEnter={(e) =>
                      (e.target.style.background = "#F3F4F6")
                    }
                    onMouseLeave={(e) =>
                      (e.target.style.background = "transparent")
                    }
                    onClick={() => handleRecentSearchClick(search)}
                  >
                    <span
                      style={{
                        fontFamily: '"Red Hat Display", sans-serif',
                        fontWeight: 400,
                        fontSize: "16px",
                        lineHeight: "24px",
                        color: "#303E4E",
                      }}
                    >
                      {search}
                    </span>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removeRecentSearch(search);
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#9CA3AF",
                        fontSize: "18px",
                        cursor: "pointer",
                        width: "24px",
                        height: "24px",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transition: "all 0.2s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = "#F3F4F6";
                        e.target.style.color = "#6B7280";
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = "transparent";
                        e.target.style.color = "#9CA3AF";
                      }}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Loading State */}
          {isSearching && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: "60px 20px",
              }}
            >
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  border: "4px solid #E5E7EB",
                  borderTop: `4px solid ${colors.primary}`,
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                  marginBottom: "16px",
                }}
              />
              <p
                style={{
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontWeight: 500,
                  fontSize: "16px",
                  lineHeight: "24px",
                  color: "#6B7280",
                  margin: 0,
                }}
              >
                Searching for "{searchQuery}"...
              </p>
            </div>
          )}

          {/* Empty State - when no recent searches */}
          {recentSearches.length === 0 && !searchQuery && !isSearching && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: "60px 20px",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  width: "80px",
                  height: "80px",
                  background: "#F3F4F6",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: "24px",
                }}
              >
                <svg width="40" height="40" viewBox="0 0 24 24" fill="#9CA3AF">
                  <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
                </svg>
              </div>

              <h3
                style={{
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontWeight: 600,
                  fontSize: "20px",
                  lineHeight: "30px",
                  color: "#303E4E",
                  margin: "0 0 8px 0",
                }}
              >
                {t("navbar.No_Recent_searches")}
              </h3>

              <p
                style={{
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontWeight: 400,
                  fontSize: "16px",
                  lineHeight: "24px",
                  color: "#6B7280",
                  margin: 0,
                  maxWidth: "300px",
                }}
              >
                {t("navbar.Search_History")}
              </p>
            </div>
          )}
        </div>

        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    </div>
  );
};

export default SearchModal;
